import type { ColDef, ValueFormatterParams } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useMemo } from 'react'
import type { Sitting } from '../api/exams'
import { longDate } from '../format'
import { defaultColDef, gridTheme } from '../grid'

export interface SittingsGridProps {
  rows: Sitting[]
  pending: boolean
  onOpen: (name: string) => void
}

/**
 * Every Sitting of the Exam, newest first.
 *
 * Sorted here rather than taken in the API's order: the Sittings that matter are the ones
 * still to come, and an Exam accumulates past ones for ever, so the newest Tests from is
 * the row an Admin opens. AG Grid keeps this as rows arrive and change, and the header
 * still re-sorts on click -- an ordering, not a lock.
 *
 * The Questions Manager's grid, from the same theme and the same defaults (`../grid`), so
 * sorting and the filter row are AG Grid's rather than this screen's (app:R14). It lays
 * itself out to its rows: an Exam has a handful of Sittings, not a page of them.
 */
export function SittingsGrid({ rows, pending, onOpen }: SittingsGridProps) {
  const columns = useMemo<ColDef<Sitting>[]>(
    () => [
      { field: 'name', headerName: 'Sitting', width: 180, pinned: 'left', cellClass: 'font-bold' },
      { field: 'bookingClosesOn', headerName: 'Booking closes', width: 190, valueFormatter: date },
      { field: 'testsFrom', headerName: 'Tests from', width: 190, valueFormatter: date, sort: 'desc' },
      { field: 'testsTo', headerName: 'Tests to', flex: 1, minWidth: 190, valueFormatter: date },
    ],
    [],
  )

  return (
    <div className="mt-4 w-full">
      <AgGridReact<Sitting>
        theme={gridTheme}
        domLayout="autoHeight"
        columnDefs={columns}
        defaultColDef={defaultColDef}
        rowData={pending ? undefined : rows}
        loading={pending}
        getRowId={(row) => row.data.name}
        overlayNoRowsTemplate="No Sitting entered yet."
        // The row IS the Sitting, so clicking it opens the Sitting, as the Questions
        // Manager opens a Question.
        onRowClicked={(row) => {
          if (row.data) onOpen(row.data.name)
        }}
      />
    </div>
  )
}

function date(cell: ValueFormatterParams<Sitting, string>): string {
  return cell.value ? longDate(cell.value) : ''
}
